import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './SpreadsheetTable.css';
import {
  FaCheckSquare, FaSquare, FaMinusSquare, FaSortUp, FaSortDown,
  FaUndo, FaSave, FaTimes, FaExclamationTriangle
} from 'react-icons/fa';

/**
 * SpreadsheetTable – Reusable Google-Sheets-style editable table.
 *
 * Props:
 *   columns       – [{ key, label, editable, type('text'|'dropdown'), options, width }]
 *   data          – [{ id, ...fields }]
 *   onSave        – (changes: [{ asset_id, fields: { field: newVal } }]) => Promise
 *   onValidate    – (changes) => Promise<{ valid, errors }>  (optional)
 *   idField       – key for row identifier (default 'id')
 *   loading       – bool
 *   pageInfo      – { page, total_pages, total } (optional, for display)
 */
const SpreadsheetTable = ({
  columns = [],
  data = [],
  onSave,
  onValidate,
  idField = 'id',
  loading = false,
  pageInfo = null
}) => {
  // ─── State ─────────────────────────────────────────────────────────
  const [editedCells, setEditedCells] = useState({});       // { rowId: { field: newVal } }
  const [activeCell, setActiveCell] = useState(null);        // { row, col }
  const [editingCell, setEditingCell] = useState(null);      // { row, col }
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [validationErrors, setValidationErrors] = useState([]); // [{ asset_id, field, message }]
  const [undoStack, setUndoStack] = useState([]);
  const [saving, setSaving] = useState(false);

  const tableRef = useRef(null);
  const inputRef = useRef(null);

  const editableColIndices = useMemo(
    () => columns.reduce((acc, col, i) => (col.editable ? [...acc, i] : acc), []),
    [columns]
  );

  const modifiedRowCount = Object.keys(editedCells).length;

  // ─── Cell value resolution ─────────────────────────────────────────
  const getCellValue = useCallback((rowId, colKey) => {
    if (editedCells[rowId] && editedCells[rowId][colKey] !== undefined) {
      return editedCells[rowId][colKey];
    }
    const row = data.find(r => r[idField] === rowId);
    return row ? (row[colKey] ?? '') : '';
  }, [editedCells, data, idField]);

  const isModified = useCallback((rowId, colKey) => {
    if (!editedCells[rowId]) return false;
    return editedCells[rowId][colKey] !== undefined;
  }, [editedCells]);

  // ─── Edit handlers ─────────────────────────────────────────────────
  const handleCellChange = useCallback((rowId, colKey, newValue) => {
    const row = data.find(r => r[idField] === rowId);
    const originalValue = row ? (row[colKey] ?? '') : '';

    // Push undo
    setUndoStack(prev => [...prev.slice(-50), {
      rowId, colKey,
      oldValue: editedCells[rowId]?.[colKey] ?? originalValue
    }]);

    setEditedCells(prev => {
      const next = { ...prev };
      if (String(newValue) === String(originalValue)) {
        // Revert to original – remove from edits
        if (next[rowId]) {
          delete next[rowId][colKey];
          if (Object.keys(next[rowId]).length === 0) delete next[rowId];
        }
      } else {
        next[rowId] = { ...(next[rowId] || {}), [colKey]: newValue };
      }
      return next;
    });

    setValidationErrors(prev => prev.filter(e => !(e.asset_id === rowId && e.field === colKey)));
  }, [data, idField, editedCells]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));

    setEditedCells(prev => {
      const next = { ...prev };
      const row = data.find(r => r[idField] === last.rowId);
      const originalValue = row ? (row[last.colKey] ?? '') : '';

      if (String(last.oldValue) === String(originalValue)) {
        if (next[last.rowId]) {
          delete next[last.rowId][last.colKey];
          if (Object.keys(next[last.rowId]).length === 0) delete next[last.rowId];
        }
      } else {
        next[last.rowId] = { ...(next[last.rowId] || {}), [last.colKey]: last.oldValue };
      }
      return next;
    });
  }, [undoStack, data, idField]);

  const handleCancelEdits = useCallback(() => {
    setEditedCells({});
    setUndoStack([]);
    setValidationErrors([]);
    setEditingCell(null);
  }, []);

  // ─── Save ──────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!onSave || modifiedRowCount === 0) return;

    const changes = Object.entries(editedCells).map(([rowId, fields]) => ({
      asset_id: parseInt(rowId),
      fields
    }));

    // Validate first if handler provided
    if (onValidate) {
      const valResult = await onValidate(changes);
      if (!valResult.valid) {
        setValidationErrors(valResult.errors || []);
        return;
      }
    }

    setSaving(true);
    try {
      await onSave(changes);
      setEditedCells({});
      setUndoStack([]);
      setValidationErrors([]);
      setEditingCell(null);
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  }, [onSave, onValidate, editedCells, modifiedRowCount]);

  // ─── Selection ─────────────────────────────────────────────────────
  const toggleRowSelection = useCallback((rowId) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(rowId) ? next.delete(rowId) : next.add(rowId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedRows.size === data.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(data.map(r => r[idField])));
    }
  }, [selectedRows, data, idField]);

  // ─── Keyboard Navigation ──────────────────────────────────────────
  const startEditing = useCallback((row, col) => {
    if (!columns[col]?.editable) return;
    setEditingCell({ row, col });
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [columns]);

  const stopEditing = useCallback(() => {
    setEditingCell(null);
  }, []);

  const moveActive = useCallback((dRow, dCol) => {
    if (!activeCell) return;
    const newRow = Math.max(0, Math.min(data.length - 1, activeCell.row + dRow));
    let newCol = activeCell.col + dCol;

    // When tabbing, skip non-editable columns
    if (dCol !== 0) {
      while (newCol >= 0 && newCol < columns.length && !columns[newCol]?.editable) {
        newCol += dCol > 0 ? 1 : -1;
      }
      if (newCol < 0 || newCol >= columns.length) return;
    }

    setActiveCell({ row: newRow, col: newCol });
    setEditingCell(null);
  }, [activeCell, data.length, columns]);

  const handleKeyDown = useCallback((e) => {
    if (!activeCell) return;

    if (editingCell) {
      if (e.key === 'Escape') { stopEditing(); e.preventDefault(); return; }
      if (e.key === 'Enter') { stopEditing(); moveActive(1, 0); e.preventDefault(); return; }
      if (e.key === 'Tab') { stopEditing(); moveActive(0, e.shiftKey ? -1 : 1); e.preventDefault(); return; }
      return;
    }

    switch (e.key) {
      case 'ArrowUp': moveActive(-1, 0); e.preventDefault(); break;
      case 'ArrowDown': moveActive(1, 0); e.preventDefault(); break;
      case 'ArrowLeft': moveActive(0, -1); e.preventDefault(); break;
      case 'ArrowRight': moveActive(0, 1); e.preventDefault(); break;
      case 'Tab': moveActive(0, e.shiftKey ? -1 : 1); e.preventDefault(); break;
      case 'Enter': startEditing(activeCell.row, activeCell.col); e.preventDefault(); break;
      case 'F2': startEditing(activeCell.row, activeCell.col); e.preventDefault(); break;
      case 'z':
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) { handleUndo(); e.preventDefault(); }
        break;
      case 's':
        if (e.ctrlKey || e.metaKey) { handleSave(); e.preventDefault(); }
        break;
      default:
        // Start editing on any printable key
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && columns[activeCell.col]?.editable) {
          startEditing(activeCell.row, activeCell.col);
        }
    }
  }, [activeCell, editingCell, moveActive, startEditing, stopEditing, handleUndo, handleSave, columns]);

  useEffect(() => {
    const el = tableRef.current;
    if (el) { el.addEventListener('keydown', handleKeyDown); }
    return () => { if (el) el.removeEventListener('keydown', handleKeyDown); };
  }, [handleKeyDown]);

  // ─── Paste handler ─────────────────────────────────────────────────
  useEffect(() => {
    const handlePaste = (e) => {
      if (!activeCell) return;
      const col = columns[activeCell.col];
      if (!col?.editable) return;

      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      const rows = text.split('\n').filter(r => r.trim());

      if (rows.length === 0) return;

      // Multi-row paste
      const newEdits = { ...editedCells };
      rows.forEach((rowText, i) => {
        const rowIdx = activeCell.row + i;
        if (rowIdx >= data.length) return;

        const rowData = data[rowIdx];
        const rowId = rowData[idField];
        const cells = rowText.split('\t');

        cells.forEach((cellVal, j) => {
          const colIdx = activeCell.col + j;
          if (colIdx >= columns.length || !columns[colIdx].editable) return;

          const colKey = columns[colIdx].key;
          const original = rowData[colKey] ?? '';
          const trimmed = cellVal.trim();

          if (!newEdits[rowId]) newEdits[rowId] = {};
          if (String(trimmed) !== String(original)) {
            newEdits[rowId][colKey] = trimmed;
          }
        });
      });

      setEditedCells(newEdits);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [activeCell, columns, data, idField, editedCells]);

  // ─── Render ────────────────────────────────────────────────────────
  const getErrorForCell = (rowId, colKey) => {
    return validationErrors.find(e => e.asset_id === rowId && e.field === colKey);
  };

  return (
    <div className="sst-wrapper" ref={tableRef} tabIndex={0}>
      {/* Toolbar */}
      {modifiedRowCount > 0 && (
        <div className="sst-toolbar">
          <div className="sst-toolbar-left">
            <span className="sst-modified-badge">
              {modifiedRowCount} row{modifiedRowCount !== 1 ? 's' : ''} modified
            </span>
            {validationErrors.length > 0 && (
              <span className="sst-error-badge">
                <FaExclamationTriangle /> {validationErrors.length} error{validationErrors.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="sst-toolbar-right">
            <button className="sst-btn-undo" onClick={handleUndo} disabled={undoStack.length === 0} title="Undo (Ctrl+Z)">
              <FaUndo /> Undo
            </button>
            <button className="sst-btn-cancel" onClick={handleCancelEdits}>
              <FaTimes /> Discard
            </button>
            <button className="sst-btn-save" onClick={handleSave} disabled={saving || validationErrors.length > 0} title="Save (Ctrl+S)">
              {saving ? 'Saving...' : <><FaSave /> Save All Changes</>}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="sst-table-scroll">
        <table className="sst-table">
          <thead>
            <tr>
              <th className="sst-th-check">
                <button className="sst-check-btn" onClick={toggleSelectAll}>
                  {selectedRows.size === data.length && data.length > 0 ? <FaCheckSquare /> :
                   selectedRows.size > 0 ? <FaMinusSquare /> : <FaSquare />}
                </button>
              </th>
              {columns.map((col, ci) => (
                <th key={col.key} style={col.width ? { width: col.width } : {}}>
                  <div className="sst-th-content">
                    {col.label}
                    {col.editable && <span className="sst-editable-dot" title="Editable" />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columns.length + 1} className="sst-loading">Loading...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={columns.length + 1} className="sst-empty">No data</td></tr>
            ) : data.map((row, ri) => {
              const rowId = row[idField];
              const rowModified = !!editedCells[rowId];

              return (
                <tr
                  key={rowId}
                  className={`${selectedRows.has(rowId) ? 'sst-row-selected' : ''} ${rowModified ? 'sst-row-modified' : ''}`}
                >
                  <td className="sst-td-check">
                    <button className="sst-check-btn" onClick={() => toggleRowSelection(rowId)}>
                      {selectedRows.has(rowId) ? <FaCheckSquare /> : <FaSquare />}
                    </button>
                  </td>
                  {columns.map((col, ci) => {
                    const isActive = activeCell?.row === ri && activeCell?.col === ci;
                    const isEditing = editingCell?.row === ri && editingCell?.col === ci;
                    const cellModified = isModified(rowId, col.key);
                    const cellError = getErrorForCell(rowId, col.key);
                    const value = getCellValue(rowId, col.key);

                    return (
                      <td
                        key={col.key}
                        className={[
                          isActive ? 'sst-cell-active' : '',
                          cellModified ? 'sst-cell-modified' : '',
                          cellError ? 'sst-cell-error' : '',
                          col.editable ? 'sst-cell-editable' : 'sst-cell-locked'
                        ].join(' ')}
                        onClick={() => {
                          setActiveCell({ row: ri, col: ci });
                          if (!isEditing) setEditingCell(null);
                        }}
                        onDoubleClick={() => startEditing(ri, ci)}
                      >
                        {isEditing && col.editable ? (
                          col.type === 'dropdown' ? (
                            <select
                              ref={inputRef}
                              className="sst-cell-input"
                              value={value}
                              onChange={(e) => handleCellChange(rowId, col.key, e.target.value)}
                              onBlur={stopEditing}
                              autoFocus
                            >
                              <option value="">—</option>
                              {(col.options || []).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              ref={inputRef}
                              type="text"
                              className="sst-cell-input"
                              value={value}
                              onChange={(e) => handleCellChange(rowId, col.key, e.target.value)}
                              onBlur={stopEditing}
                              autoFocus
                            />
                          )
                        ) : (
                          <div className="sst-cell-display">
                            <span className="sst-cell-text">{value || (col.editable ? '' : '—')}</span>
                            {cellError && (
                              <span className="sst-cell-error-tip" title={cellError.message}>
                                <FaExclamationTriangle />
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {pageInfo && (
        <div className="sst-footer">
          <span>Showing {data.length} of {pageInfo.total} rows</span>
          {pageInfo.total_pages > 1 && (
            <span>Page {pageInfo.page} of {pageInfo.total_pages}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default SpreadsheetTable;
